import React, { useEffect, useState } from 'react';
import {
  TextField,
  Box,
  Card,
  Table,
  Divider,
  TableBody,
  Container,
  TableContainer,
  TablePagination,
  TableCell,
  TableRow,
  Button,
  Typography,
} from '@mui/material';
import { PATH_DASHBOARD } from '../../../routes/paths';
import Page from '../../../components/Page';
import Scrollbar from '../../../components/Scrollbar';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import {
  TableEmptyRows,
  TableHeadCustom,
  ColumnsButton,
} from '../../../components/table';
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';
import useSettings from '../../../hooks/useSettings';
import useTable, { emptyRows } from '../../../hooks/useTable';
import useColumnVisibility, { ColumnDef } from '../../../hooks/useColumnVisibility';
import Iconify from '../../../components/Iconify';
import Label from '../../../components/Label';
import { fDateTimeSuffix } from '../../../utils/formatTime';
import CustomLoggersModal from './CustomLoggerModal';

const MONITOR_COLUMNS: ColumnDef[] = [
  { id: 'Method', label: 'Method', align: 'left' },
  { id: 'actionOriginUrl', label: 'Url', align: 'left' },
  { id: 'Email', label: 'Email', align: 'left' },
  { id: 'Handle', label: 'Action', align: 'left' },
  { id: 'Date', label: 'Date', align: 'left' },
];

// Method → chip color, for quick visual scanning of the audit log.
const METHOD_COLOR: Record<string, 'info' | 'success' | 'warning' | 'error' | 'default'> = {
  GET: 'info',
  POST: 'success',
  PATCH: 'warning',
  PUT: 'warning',
  DELETE: 'error',
};
const MONITOR_COLUMNS_STORAGE_KEY = 'kayfa.monitor.visibleColumns';

export default function CustomLoggersItems() {
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    setSelected,
    onSort,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable();
  const { themeStretch } = useSettings();
  const [tableData, setTableData] = useState<any>([]);
  const [total, setTotal] = useState<number>(0);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const { visibleColumns, toggleColumn, isVisible, headLabel } = useColumnVisibility(
    MONITOR_COLUMNS,
    MONITOR_COLUMNS_STORAGE_KEY
  );

  useEffect(() => {
    getCustomLoggers(page + 1, rowsPerPage).catch(console.error);
  }, [page, rowsPerPage]);

  const getCustomLoggers = async (
    page: number,
    limit: number,
    sortedBy?: string,
    filterOptions?: any
  ) => {
    let response;
    if (
      filterOptions?.method?.length ||
      filterOptions?.statusCode?.length ||
      filterOptions?.controllerName?.length ||
      filterOptions?.email ||
      filterOptions?.fromDate ||
      filterOptions?.toDate
    ) {
      const method = filterOptions.method?.map((item: string) => `&method=${item}`).join('');
      const statusCode = filterOptions.statusCode
        ?.map((item: string) => `&statusCode=${item}`)
        .join('');
      const controllerName = filterOptions.controllerName
        ?.map((item: string) => `&controllerName=${item}`)
        .join('');
      const email = `&email=${filterOptions.email}`;
      const fromDate = `&actionDateFrom=${filterOptions.fromDate}`;
      const toDate = `&actionDateTo=${filterOptions.toDate}`;

      response = await axiosInstance.get(
        `${APIS.SCREEN.getScreen}/?page=${page}&limit=${limit}${method}${statusCode}${controllerName}${email}${fromDate}${toDate}`
      );
    } else {
      response = await axiosInstance.get(`${APIS.SCREEN.getScreen}/?page=${page}&limit=${limit}`);
    }

    setTableData(response?.data.loggers || []);
    setTotal(response?.data.totalCount || 0);
  };

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleApplyFilters = (filterOptions: any) => {
    getCustomLoggers(page + 1, rowsPerPage, orderBy, filterOptions);
    handleCloseModal();
  };

  return (
    <Page title="Screen: List">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Screen List"
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            { name: 'Screen List', href: PATH_DASHBOARD.screens },
          ]}
          action={
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <ColumnsButton
                columns={MONITOR_COLUMNS}
                visibleColumns={visibleColumns}
                onToggle={toggleColumn}
              />
              <Button
                variant="contained"
                onClick={handleOpenModal}
                startIcon={<Iconify icon={'eva:plus-fill'} />}
              >
                Filter
              </Button>
            </Box>
          }
        />
      </Container>
      <Card>
        <Divider />

        <Scrollbar>
          <TableContainer sx={{ minWidth: 800, position: 'relative' }}>
            <Table size={dense ? 'small' : 'medium'}>
              <TableHeadCustom
                order={order}
                orderBy={orderBy}
                headLabel={headLabel}
                rowCount={tableData?.length}
                onSort={onSort}
              />
              <TableBody>
                {tableData?.length > 0 ? (
                  tableData.map((row: any) => (
                    <TableRow key={row._id} hover>
                      {isVisible('Method') && (
                        <TableCell>
                          <Label color={METHOD_COLOR[row?.method] || 'default'}>
                            {row?.method || '—'}
                          </Label>
                        </TableCell>
                      )}
                      {isVisible('actionOriginUrl') && (
                        <TableCell sx={{ maxWidth: 340 }}>
                          <Typography variant="body2" noWrap title={row.actionOriginUrl}>
                            {row.actionOriginUrl || '—'}
                          </Typography>
                        </TableCell>
                      )}
                      {isVisible('Email') && <TableCell>{row?.email || '—'}</TableCell>}
                      {isVisible('Handle') && <TableCell>{row?.handle || '—'}</TableCell>}
                      {isVisible('Date') && (
                        <TableCell>
                          {row?.actionDate
                            ? fDateTimeSuffix(new Date(row.actionDate))
                            : '—'}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length || 1} align="center">
                      No data available
                    </TableCell>
                  </TableRow>
                )}
                <TableEmptyRows
                  height={2.2}
                  emptyRows={emptyRows(page, rowsPerPage, tableData?.length)}
                />
              </TableBody>
            </Table>
          </TableContainer>
        </Scrollbar>

        <Box sx={{ position: 'relative' }}>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
          />
        </Box>
      </Card>
      <CustomLoggersModal
        open={modalOpen}
        onClose={handleCloseModal}
        onApplyFilters={handleApplyFilters}
      />
    </Page>
  );
}
